// useFreteDoLivro.js — o estado do frete da página do livro.
//
// ================= POR QUE ELE SAIU DE DENTRO DO CARTÃO (18/09/2026) =================
//
// O CEP e as modalidades moravam dentro do PrecoCard. No computador isso deixava
// duas coisas erradas ao mesmo tempo: o cartão de preço ficava alto (o botão de
// comprar descia para perto do fim da tela) e a coluna da capa terminava cedo,
// com um vão de uns 200px de nada embaixo dela — foi o que o dono marcou de
// verde no print.
//
// Agora o frete é desenhado FORA do cartão no computador (embaixo da capa, onde
// havia o vão) e DENTRO dele no celular, onde a coluna da capa vem antes do
// título e um cálculo de frete ali em cima chegaria antes até do preço.
//
// O ESTADO NÃO PODE SER DUPLICADO, e é por isso que ele mora aqui em vez de
// dentro do componente de desenho: quem escolhe a modalidade é a pessoa, mas
// quem PRECISA do valor é o item que vai para o carrinho (montado no PrecoCard)
// e a validação do "COMPRAR AGORA". Dois estados — um por posição — dariam um
// frete escolhido na tela e outro no carrinho, que é a classe de divergência
// que esta página já pagou caro para eliminar nos preços.
import { useEffect, useRef, useState } from "react";
import useProfileData from "../../../hooks/userProfileData";
import { mascaraCep, soDigitosCep } from "../../../utils/cep";

/** As duas modalidades, na única fonte. O carrinho recalcula pelo total. */
const MODALIDADES = {
  20: { valor: 20, dias: 20, chave: "padrao", rotulo: "Padrão (20 dias)" },
  70: { valor: 70, dias: 12, chave: "expresso", rotulo: "Expresso (12 dias)" },
};

export default function useFreteDoLivro() {
  const [cep, setCep] = useState("");
  const [mostrar, setMostrar] = useState(false);
  const [erro, setErro] = useState("");
  const [selecionada, setSelecionada] = useState("20");

  // ===== O CEP DO PERFIL, JÁ NO CAMPO =====
  //
  // Quem tem conta já disse o CEP uma vez, no perfil. Pedir de novo em cada
  // livro é o atrito que faz a pessoa não olhar o frete — e frete desconhecido
  // é o motivo número um de quem desiste do impresso.
  //
  // UMA VEZ SÓ, e só com o campo vazio (`jaAplicado`): se a pessoa apagar o CEP
  // para testar outro endereço, ele não pode voltar sozinho por cima do que ela
  // está digitando.
  //
  // Visitante sem conta: o hook não busca nada sem token, e o campo continua
  // vazio esperando digitação.
  const { profileData } = useProfileData();
  const jaAplicado = useRef(false);

  useEffect(() => {
    if (jaAplicado.current) return;
    const salvo = soDigitosCep(profileData?.cep);
    if (salvo.length !== 8) return;

    jaAplicado.current = true;
    if (!soDigitosCep(cep)) {
      setCep(mascaraCep(salvo));
      // O frete desta loja é por modalidade, não por distância: com um CEP
      // válido, "calcular" é mostrar as opções. Então elas já abrem.
      setMostrar(true);
      setErro("");
    }
  }, [profileData, cep]);

  /** Digitação: com menos de 8 dígitos as opções fecham. */
  const digitarCep = (valor) => {
    const v = mascaraCep(valor);
    setCep(v);
    if (soDigitosCep(v).length < 8) setMostrar(false);
  };

  const calcular = () => {
    if (soDigitosCep(cep).length >= 8) {
      setMostrar(true);
      setErro("");
    } else {
      setErro("Por favor, insira um CEP válido.");
    }
  };

  /** Chamado antes de adicionar ao carrinho. false = não pode seguir. */
  const validar = () => {
    if (!mostrar) {
      setErro("Calcule o frete antes de adicionar ao carrinho.");
      return false;
    }
    setErro("");
    return true;
  };

  /** Trocar de formato fecha o cálculo: ele é do impresso. */
  const limpar = () => {
    setMostrar(false);
    setErro("");
  };

  /** O que viaja com o item do carrinho — ou nada, se não foi calculado. */
  const paraOCarrinho = () => {
    if (!mostrar) return null;
    const m = MODALIDADES[selecionada] || MODALIDADES[20];
    return { valor: m.valor, dias: m.dias, cep, modalidade: m.rotulo, chave: m.chave };
  };

  return {
    cep,
    digitarCep,
    mostrar,
    erro,
    selecionada,
    escolher: setSelecionada,
    calcular,
    validar,
    limpar,
    paraOCarrinho,
    modalidades: MODALIDADES,
  };
}
